/**
 * Integrations Panel Component
 *
 * User-facing UI for managing Composio integrations:
 * - Browse available apps/toolkits (one ranked omnisearch, 2026-08-18)
 * - Connect/disconnect accounts via OAuth
 * - View connected account status
 *
 * 2026-08-18 UX overhaul: presentation decomposed into ./panel/* (AppTile,
 * SearchBar, CategoryChips, SetupFieldsDialog, PanelModal) on the shared
 * warm tokens (panelTheme.ts); search runs through the shared scored
 * matcher (appSearch.ts) — the old exact-match "connect any system" hero is
 * now the omnisearch's always-visible custom row, so a typo'd catalog app
 * can never silently register a custom connection again.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  listToolkits,
  getConnectedAccounts,
  getAccountsTwoSets,
  shareConnection,
  disconnectToolkit,
  disconnectAccount,
  FEATURED_TOOLKITS,
  TOOLKIT_CATEGORIES,
  familyForToolkit,
  sortByLikelihood,
  MORE_APPS_FAMILY,
  isStorageToolkit,
  supportsFolderScope,
  getStorageScope,
  getAccessDetail,
  setAccessTier,
  setAccessProfile,
  ACCESS_TIER_LABELS,
  accessChipLabel,
  markAddAccountLaunch,
  readAddAccountLaunch,
  clearAddAccountLaunch,
  listCustomConnections,
  updateCustomConnectionIdentity,
  removeCustomConnection,
  recordPendingConnection,
  clearPendingConnection,
  type CustomConnection,
  type ComposioToolkit,
  type ConnectedAccount,
  type TwoSetAccountsResult,
  type StorageScope,
  type AccessTier,
  type AccessDetail,
  type AccessProfile,
  getConnectRequiredFields,
  type ConnectInitField,
  type ConnectionVerification,
} from './ComposioClient';
import { isEmailJourneySlug } from './providerJourneyMeta';
import {
  buildAdminHandoffNote,
  buildMicrosoftAdminConsentUrlFromClientId,
  providerForEmailToolkitSlug,
} from './adminHandoff';
import EmailIntentPrompt from './panel/EmailIntentPrompt';
import { searchToolkits } from './appSearch';
import { peekConnectLane, laneIsDirect, laneExplainer } from './connectLane';
import { customSlugForName } from './desktopSignIn';
import { reconcilePrest0nConnections } from '../prest0nSession/client';
// Connected-apps store freshness (task 5, 2026-08-12): chips/grounding read a
// 2-min TTL cache — a connect or disconnect made HERE must be visible to the
// chat surfaces immediately, not after the TTL runs out.
import { invalidateConnectedAppsCache } from '../threadRecommendations/connectedAppsStore';
// Storage folder scope sheet (2026-08-11): "What can Preston use in <App>?"
import FolderScopePicker from './FolderScopePicker';
import { useAuth } from '../../context/AuthContext';
import { useRbac } from '../../hooks/useRbac';
import panelTheme from './panelTheme';
import AppTile from './panel/AppTile';
import SearchBar from './panel/SearchBar';
import CategoryChips from './panel/CategoryChips';
import SetupFieldsDialog, { type SetupPromptState } from './panel/SetupFieldsDialog';
import ConnectStatusLine from './panel/ConnectStatusLine';
import ConnectionDoneCard from './panel/ConnectionDoneCard';
import ConnectedSection from './panel/ConnectedSection';
import { CONNECT_SIGNED_IN_EVENT } from './connectSignedInEvent';
import ManageAppModal from './panel/ManageAppModal';
import AccessModal from './panel/AccessModal';
import useConnectPopup from './useConnectPopup';
import { logConnectFunnel } from './connectPopupFlow';
// The loud "the window will say Composio" explainer — shown on EVERY surface
// while a connect popup is in flight (2026-08-18; previously onboarding-only).

interface IntegrationsPanelProps {
  /** Optional class name for styling */
  className?: string;
  /** Callback when a new connection is established */
  onConnectionChange?: () => void;
}

// Two-set model (2026-08-13 owner directive): the panel shows either "my
// connections" (the member's own accounts) or "organization" (workspace-owned
// + member-shared connections). Raw rows arrive uncollapsed from getAccountsTwoSets.
type IntegrationsView = 'personal' | 'organization';

interface CollapsedConnection extends ConnectedAccount {
  /** Personal view: true when ANY of the tile's connections is shared with the org. */
  sharedWithOrg?: boolean;
  /** Org view: true when ANY of the tile's connections came in via a member share. */
  viaShared?: boolean;
  /** Org view: the sharer's display NAME (never a uid), when resolved. */
  sharedByName?: string;
}

/** Collapse raw rows per toolkit slug — earliest ACTIVE is the primary with
 *  the active roster attached (same convention as getConnectedAccounts). */
function collapsePerSlug(
  rows: Array<ConnectedAccount & { sharedWithOrg?: boolean; via?: 'organization' | 'shared'; sharedBy?: string; sharedByName?: string }>
): CollapsedConnection[] {
  const bySlug = new Map<string, typeof rows>();
  for (const row of rows) {
    const slug = row.toolkit.slug.toLowerCase();
    if (!slug) continue;
    (bySlug.get(slug) ?? bySlug.set(slug, []).get(slug)!).push(row);
  }
  const result: CollapsedConnection[] = [];
  for (const group of bySlug.values()) {
    const actives = group
      .filter((r) => r.status?.toUpperCase() === 'ACTIVE')
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const primary = actives[0] || group[0];
    const roster = actives.length > 0 ? actives : group;
    result.push({
      id: primary.id,
      status: primary.status,
      toolkit: primary.toolkit,
      created_at: primary.created_at,
      // Roster carries each connection's IDENTITY (email/phone) so the
      // manage popup can tell accounts apart (2026-08-18) — plus share
      // attribution so identity-less shared rows can say "shared by <name>"
      // instead of a positional "account N" (2026-08-20).
      connections: actives.map((r) => ({
        id: r.id,
        created_at: r.created_at,
        identity: r.identity,
        ...(typeof (r as any).mailboxOk === 'boolean' ? { mailboxOk: (r as any).mailboxOk } : {}),
        ...(r.via === 'shared' || r.sharedBy || r.sharedByName ? { via: 'shared' as const } : {}),
        ...(r.sharedByName ? { sharedByName: r.sharedByName } : {}),
      })),
      ...(primary.identity ? { identity: primary.identity } : {}),
      ...(typeof (primary as any).mailboxOk === 'boolean'
        ? { mailboxOk: (primary as any).mailboxOk }
        : {}),
      sharedWithOrg: roster.some((r) => !!r.sharedWithOrg),
      // W-R3 (2026-08-25): the union of workspaces any roster row is lent
      // to — powers the personal workspace's multi-org share picker.
      ...(roster.some((r) => Array.isArray((r as any).sharedTo) && (r as any).sharedTo.length)
        ? { sharedTo: Array.from(new Set(roster.flatMap((r) => ((r as any).sharedTo as string[] | undefined) || []))) }
        : {}),
      // "shared by …" keys on presence; attribution is by NAME only — the
      // raw uid never renders (house rule).
      viaShared: roster.some((r) => r.via === 'shared' || !!r.sharedBy || !!r.sharedByName),
      ...(roster.map((r) => r.sharedByName).find(Boolean)
        ? { sharedByName: roster.map((r) => r.sharedByName).find(Boolean) }
        : {}),
    });
  }
  return result;
}

export const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({
  className = '',
  onConnectionChange,
}) => {
  const { user, isAuthenticated } = useAuth();
  const { hasPermission, loading: rbacLoading } = useRbac();
  const [toolkits, setToolkits] = useState<ComposioToolkit[]>([]);
  // Two-set model: raw rows per set; the rendered `connectedAccounts` are the
  // ACTIVE view's rows collapsed per slug (below).
  const [twoSets, setTwoSets] = useState<TwoSetAccountsResult>({ personal: [], orgVisible: false });
  const [viewMode, setViewMode] = useState<IntegrationsView>('personal');
  const [toolkitsLoading, setToolkitsLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  // Per-slug pending disconnect (2026-08-12): only the tile being
  // disconnected shows "…"/disabled — every other tile stays interactive.
  const [disconnectingSlug, setDisconnectingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Custom (browser-signin) connections — any system outside the catalog
  // (2026-08-13 owner directive). Metadata only; the sign-in itself happens
  // on Prest0n's own browser.
  const [customConnections, setCustomConnections] = useState<CustomConnection[]>([]);
  const [customBusy, setCustomBusy] = useState(false);

  const hasAuthContext = isAuthenticated && !!user;

  // Two-set derivations: the organization tab only exists when the server says
  // so; the personal set collapses per slug exactly like getConnectedAccounts.
  const orgVisible = twoSets.orgVisible;
  const activeView: IntegrationsView = orgVisible ? viewMode : 'personal';
  const personalCollapsed = useMemo(() => collapsePerSlug(twoSets.personal), [twoSets.personal]);
  const orgCollapsed = useMemo(() => collapsePerSlug(twoSets.organization || []), [twoSets.organization]);
  const connectedAccounts = activeView === 'organization' ? orgCollapsed : personalCollapsed;

  // Swallowed-first-click fix (2026-08-12): a window-focus refetch fires on
  // mousedown and used to flip toolkitsLoading/accountsLoading true, which
  // unmounted the whole grid (skeleton) and replaced the X with "Checking
  // status..." — eating the click. Full skeleton ONLY on the first load;
  // refetches keep rendering the previous data (subtle "refreshing" hint) so
  // the X stays mounted.
  const hasLoadedOnceRef = useRef(false);

  const loadData = useCallback(async () => {
    if (!hasAuthContext) {
      return;
    }
    setError(null);
    const firstLoad = !hasLoadedOnceRef.current;
    if (firstLoad) {
      setToolkitsLoading(true);
      setAccountsLoading(true);
    } else {
      setRefreshing(true);
    }

    const toolkitsPromise = listToolkits()
      .then((tk) => {
        setToolkits(tk);
      })
      .catch((err: any) => {
        setError(err.message || 'Failed to load integrations');
      })
      .finally(() => {
        setToolkitsLoading(false);
      });

    const accountsPromise = getAccountsTwoSets()
      .then((sets) => {
        setTwoSets(sets);
      })
      .catch((err: any) => {
        // Keep previously-rendered accounts on a refetch failure — only the
        // first load may fall back to empty (there is nothing better to show).
        if (firstLoad) setTwoSets({ personal: [], orgVisible: false });
        setError((prev) => prev || err.message || 'Failed to load integrations');
      })
      .finally(() => {
        setAccountsLoading(false);
      });

    // Custom connections ride the same load — non-fatal (an older backend
    // without the endpoint just leaves the list empty).
    const customPromise = listCustomConnections()
      .then((customs) => {
        setCustomConnections(customs);
        // Truth verifier (2026-08-20; rescue widen 2026-08-22): fire-and-
        // forget re-check of the browser-lane logins whenever ANY custom
        // connection exists — connected ones get truth-checked, and a
        // pending one whose browser already holds the login gets PROMOTED
        // (the abandoned-attestation rescue). The server throttles (6h) and
        // never wakes a sleeping VM, so this is cheap. Deliberately after
        // the list is already rendered — truth refreshes in the background,
        // it never blocks the page.
        if (customs.length > 0) {
          void import('./desktopSignIn').then((m) => m.verifyCustomSignins());
        }
      })
      .catch(() => { /* endpoint optional */ });

    await Promise.allSettled([toolkitsPromise, accountsPromise, customPromise]);
    hasLoadedOnceRef.current = true;
    setRefreshing(false);
  }, [hasAuthContext]);

  useEffect(() => {
    if (hasAuthContext) {
      loadData();
      // Connect-intent warmup (2026-08-31 instant-connect): opening the
      // integrations page is workspace-level connect intent — kick the
      // workstation boot check / wake and warm the VM transport now, so a
      // connect started from this page finds everything already paid for.
      // No session pre-create here (no specific app yet).
      void import('./connectIntentWarmup')
        .then(({ warmUpConnectIntent }) => warmUpConnectIntent({}))
        .catch(() => undefined);
    }
  }, [loadData, hasAuthContext]);

  // Auto-refresh when user returns to the page (e.g., after completing OAuth in another tab)
  useEffect(() => {
    if (!hasAuthContext) return;
    const handleFocus = () => { loadData(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadData, hasAuthContext]);

  // THE ONE COMPLETION SIGNAL (2026-09-03). This page used to learn about a
  // finished sign-in through its own modal's onConnected prop — which is
  // exactly why it only ever heard about sign-ins IT had opened. The surface
  // now announces, so this page refreshes and shows its done card whether the
  // user signed in from here, from a chat card floating over this page, or
  // from onboarding.
  useEffect(() => {
    const onSignedIn = (e: Event) => {
      const detail = (e as CustomEvent).detail as { appName?: string; customSlug?: string } | undefined;
      void loadData();
      onConnectionChange?.();
      const slug = detail?.customSlug ? String(detail.customSlug) : '';
      if (!slug) return;
      // The same "done → wow" beat direct connects get (2026-08-19): one
      // compact card — connected, defaults stated, try-it prompt.
      logConnectFunnel({ event: 'custom_done_card', toolkitSlug: slug });
      setDoneCard({
        slug,
        displayName: String(detail?.appName || slug).slice(0, 80),
        isStorage: false,
        customConnection: true,
      });
    };
    document.addEventListener(CONNECT_SIGNED_IN_EVENT, onSignedIn as EventListener);
    return () => document.removeEventListener(CONNECT_SIGNED_IN_EVENT, onSignedIn as EventListener);
  }, [loadData, onConnectionChange]);

  // Pending-watch (2026-08-22 owner directive: when the user finishes signing
  // in, the tile must flip green WITHOUT a reload). While any custom
  // connection is not yet 'connected', re-fetch just the custom list every
  // few seconds — a promotion landing from the sign-in surface's auto-
  // completion, the verifier rescue, or another device shows up live. Stops
  // the moment nothing is pending, and caps itself per visit (adversarial
  // review: an ABANDONED registration would otherwise hot-loop forever) —
  // after the cap, the window-focus refetch is the ambient fallback.
  const hasPendingCustom = customConnections.some((c) => c.status !== 'connected');
  useEffect(() => {
    if (!hasAuthContext || !hasPendingCustom) return;
    let ticks = 0;
    const timer = window.setInterval(() => {
      ticks += 1;
      if (ticks > 100) { // ~10 minutes per page visit
        window.clearInterval(timer);
        return;
      }
      void listCustomConnections()
        .then((fresh) => setCustomConnections(fresh))
        .catch(() => { /* transient — next tick retries */ });
    }, 6000);
    return () => window.clearInterval(timer);
  }, [hasAuthContext, hasPendingCustom]);

  // Storage folder scopes (2026-08-11): every connected storage app (Drive,
  // Dropbox, Box, OneDrive, ...) carries a "What can Preston use?" scope.
  // Cached here per slug for the chip on each card (`Everything` vs `📁 N
  // folders`). Reads are member-visible by design; edits go through
  // FolderScopePicker and require integrations.manage (server-enforced).
  const [storageScopes, setStorageScopes] = useState<Record<string, StorageScope>>({});
  const [scopePickerSlug, setScopePickerSlug] = useState<string | null>(null);
  // Multi-account (task #52): when set, the picker browses/saves THIS
  // connection instead of the primary. Set only by the add-another-account
  // flow; cleared whenever the picker closes.
  const [scopePickerConnectionId, setScopePickerConnectionId] = useState<string | null>(null);
  // Targeted picker read-back (2026-08-20): a specific connection's stored
  // scope, fetched before the picker mounts so re-opening it shows the
  // account's CURRENT selection instead of blank defaults. { loaded: false }
  // gates the mount; a failed read opens the picker on its defaults.
  const [pickerConnectionScope, setPickerConnectionScope] = useState<{ loaded: boolean; scope: StorageScope | null }>({ loaded: false, scope: null });
  const scopeFetchesInFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!scopePickerSlug || !scopePickerConnectionId) {
      setPickerConnectionScope({ loaded: false, scope: null });
      return;
    }
    let cancelled = false;
    getStorageScope(scopePickerSlug, scopePickerConnectionId)
      .then((scope) => { if (!cancelled) setPickerConnectionScope({ loaded: true, scope }); })
      .catch(() => { if (!cancelled) setPickerConnectionScope({ loaded: true, scope: null }); });
    return () => { cancelled = true; };
  }, [scopePickerSlug, scopePickerConnectionId]);

  useEffect(() => {
    // BOTH views (owner report 2026-08-20: a folder-scoped Drive read
    // "Full access" on personal tiles — the scope was only ever fetched for
    // the organization view, so the access chip had nothing to refine with).
    // Same lazy per-slug pattern as the access-detail effect below.
    [...orgCollapsed, ...personalCollapsed]
      .filter((a) => a.status?.toUpperCase() === 'ACTIVE' && isStorageToolkit(a.toolkit.slug))
      .forEach((a) => {
        const slug = a.toolkit.slug.toLowerCase();
        if (storageScopes[slug] || scopeFetchesInFlight.current.has(slug)) return;
        scopeFetchesInFlight.current.add(slug);
        getStorageScope(slug)
          .then((scope) => setStorageScopes((prev) => ({ ...prev, [slug]: scope })))
          .catch(() => {
            // Chip stays blank; the next accounts refresh (focus/loadData) retries.
            scopeFetchesInFlight.current.delete(slug);
          });
      });
  }, [orgCollapsed, personalCollapsed, storageScopes]);

  // Access details (2026-08-12 tiers; 2026-08-16 capability profiles): every
  // connected app carries an access tier + per-class tool counts for the
  // fine-tune sheet. Fetched lazily per connected slug (same pattern as
  // storage scopes); reads are member-visible, edits require
  // integrations.manage (server-enforced).
  const [accessDetails, setAccessDetails] = useState<Record<string, AccessDetail>>({});
  const [accessMenuSlug, setAccessMenuSlug] = useState<string | null>(null);
  /** The open sheet's access fetch failed (2026-08-25 adversarial review):
   *  the sheet says so honestly instead of "loading access…" forever. */
  const [accessDetailFailedSlug, setAccessDetailFailedSlug] = useState<string | null>(null);
  const [accessSavingSlug, setAccessSavingSlug] = useState<string | null>(null);
  const accessFetchesInFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Access chips render in BOTH views (2026-08-14 owner report: the
    // "my connections" Gmail tile said "61 tools • Connected" but never which
    // access tier Preston holds). The tier is the workspace's per-app policy —
    // one value either way — fetched once per connected slug.
    [...orgCollapsed, ...personalCollapsed]
      .filter((a) => a.status?.toUpperCase() === 'ACTIVE')
      .forEach((a) => {
        const slug = a.toolkit.slug.toLowerCase();
        if (accessDetails[slug] || accessFetchesInFlight.current.has(slug)) return;
        accessFetchesInFlight.current.add(slug);
        getAccessDetail(slug)
          .then((detail) => setAccessDetails((prev) => ({ ...prev, [slug]: detail })))
          .catch(() => {
            // Chip stays blank; the next accounts refresh (focus/loadData) retries.
            accessFetchesInFlight.current.delete(slug);
          });
      });
  }, [orgCollapsed, personalCollapsed, accessDetails]);

  useEffect(() => {
    // AN OPEN SHEET ALWAYS LOADS (2026-08-25 adversarial review). The lazy
    // effect above only fetches for ACTIVE-connected slugs, so every other
    // way the sheet opens — the ?manage / ?setup deep links, an unconnected
    // app — used to render permanently inert controls under a "loading
    // access…" line that nothing was loading. One fetch keyed to whatever
    // sheet is open covers every entry point, including a failed first try
    // on reopen. The access endpoint answers for ANY slug (default full).
    const slug = accessMenuSlug;
    if (!slug || accessDetails[slug]) return;
    let cancelled = false;
    setAccessDetailFailedSlug(null);
    getAccessDetail(slug)
      .then((detail) => {
        if (cancelled) return;
        setAccessDetails((prev) => ({ ...prev, [slug]: detail }));
      })
      .catch(() => {
        if (cancelled) return;
        // Honest failure beats an eternal spinner; reopening retries.
        setAccessDetailFailedSlug(slug);
      });
    return () => { cancelled = true; };
  }, [accessMenuSlug, accessDetails]);

  /** Store a fresh access value, keeping the already-fetched class counts —
   *  a tier/profile save never round-trips the (static) capability map. */
  const storeAccessUpdate = (slug: string, access: AccessDetail['access']) => {
    setAccessDetails((prev) => ({
      ...prev,
      [slug]: {
        access,
        classCounts: prev[slug]?.classCounts ?? null,
        classExamples: prev[slug]?.classExamples ?? null,
      },
    }));
  };

  const handleSetAccessTier = async (slug: string, tier: AccessTier) => {
    try {
      setAccessSavingSlug(slug);
      setError(null);
      const access = await setAccessTier(slug, tier);
      storeAccessUpdate(slug, access);
      setAccessMenuSlug(null);
    } catch (err: any) {
      setError(err.message || 'Failed to change access');
    } finally {
      setAccessSavingSlug(null);
    }
  };

  // Fine-tune save (2026-08-16): unlike a preset pick, the sheet stays OPEN —
  // fine-tuning is usually several toggles in a row.
  const handleSetAccessProfile = async (slug: string, profile: AccessProfile) => {
    try {
      setAccessSavingSlug(slug);
      setError(null);
      const access = await setAccessProfile(slug, profile);
      storeAccessUpdate(slug, access);
    } catch (err: any) {
      setError(err.message || 'Failed to change access');
    } finally {
      setAccessSavingSlug(null);
    }
  };

  // One chip, both views (2026-08-14): the access chip is identical on
  // organization and personal tiles — the tier is a single workspace policy
  // per app. Clicking opens the ACCESS MODAL (2026-08-18 — a proper centered
  // popup replacing the cramped 270px anchored popover). Edits stay
  // manage-gated; members see the chip read-only with the "Ask an owner or
  // admin" tooltip.
  const renderAccessChip = (slug: string, opts?: { scopeAware?: boolean }) => {
    const detail = accessDetails[slug];
    const access = detail?.access;
    // Scope-aware by default (owner report 2026-08-20: a Drive limited to
    // chosen folders still read "Full access"): wherever this chip is the
    // ONLY permission chip, a folder-scoped storage app reads "Certain
    // folders". Surfaces that render their own 📁 chip right beside it pass
    // scopeAware: false so the row never says "folders" twice.
    const scope = opts?.scopeAware !== false && isStorageToolkit(slug)
      ? (storageScopes[slug] || null)
      : null;
    const folderScoped = scope?.mode === 'folders';
    // Label covers both axes ("Custom" when the stored profile matches no
    // preset, 2026-08-16; "certain folders" when the scope is narrowed).
    const accessLabel = accessChipLabel(access, scope);
    const accessClickable = !!access && canManage && !rbacLoading;
    // One Drive scope governs Docs & Sheets too (corpus collapse — see
    // functions/src/composio/storageScope.ts).
    const corpusNote = folderScoped && (slug === 'googledocs' || slug === 'googlesheets')
      ? ' Your Google Drive folder choices govern Docs & Sheets.'
      : '';
    const folderCount = scope?.folders.length || 0;
    return (
      <button
        onClick={() => {
          if (!accessClickable) return;
          setAccessMenuSlug(accessMenuSlug === slug ? null : slug);
        }}
        disabled={!accessClickable}
        data-testid={`access-chip-${slug}`}
        title={!access
          ? 'Loading access...'
          : rbacLoading
            ? 'Checking permissions...'
            : canManage
              ? (folderScoped
                  ? `${access.custom ? 'Custom abilities' : access.tier === 'full' ? 'Full abilities' : ACCESS_TIER_LABELS[access.tier]} inside ${folderCount} chosen folder${folderCount === 1 ? '' : 's'} — click to change.${corpusNote}`
                  : 'Change what Prest0n may do here')
              : 'Ask an owner or admin'}
        style={{
          // Warm palette only (task #55): narrower tiers get a gold
          // outline instead of switching to blue/gray.
          fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
          borderRadius: panelTheme.radiusChip,
          border: access && (access.custom || access.tier !== 'full' || folderScoped) ? `1px solid ${panelTheme.gold}` : '1px solid transparent',
          cursor: accessClickable ? 'pointer' : 'not-allowed',
          background: panelTheme.goldTintStrong,
          color: panelTheme.goldInk,
          opacity: !access ? 0.6 : 1,
        }}
      >
        🔑 {accessLabel}
      </button>
    );
  };

  // Check for callback success params in URL
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const app = params.get('appName');
    if (status === 'success' && app) {
      // Default-full-access copy (2026-08-12): non-storage apps get the
      // access confirmation here; storage apps open FolderScopePicker below,
      // which carries its own default-access line.
      setSuccessMessage(
        supportsFolderScope(app)
          ? `${app} connected successfully!`
          : `${app} connected — Prest0n has full access. Change it anytime with the 🔑 button on its tile.`
      );
      // Clean up URL params
      window.history.replaceState({}, '', '/integrations');
      // Refresh to show new connection
      loadData();
      // Chat surfaces ground against a 2-min TTL cache — drop it so the new
      // connection is visible to suggestions immediately (task 5, 2026-08-12).
      invalidateConnectedAppsCache();
      // Tell the assistant immediately (capability reconcile) — otherwise a
      // connect made here isn't visible in chat until some other surface
      // polls (T1 live test, 2026-08-12: "it still doesn't have access to my
      // Gmail"). Best-effort; the portal poll reconcile is the backstop.
      void reconcilePrest0nConnections().catch(() => { /* best-effort */ });
      // Add-another-account works for EVERY app (task #55 — Gmail and
      // Calendar included). Non-storage apps have nothing per-connection to
      // configure, so their add-mark just clears; storage continues into the
      // per-connection folder picker below.
      if (!supportsFolderScope(app)) {
        const addMark = readAddAccountLaunch();
        if (addMark && addMark.slug === app.toLowerCase()) clearAddAccountLaunch();
      }
      // Storage scope sheet (2026-08-11): a storage app just finished OAuth —
      // ask the one scope question right away. Dismissing without saving is
      // safe: the server grandfathers the connection as "Everything".
      if (supportsFolderScope(app)) {
        const slug = app.toLowerCase();
        const addMark = readAddAccountLaunch();
        getStorageScope(slug)
          .then((scope) => {
            setStorageScopes((prev) => ({ ...prev, [slug]: scope }));
            if (addMark && addMark.slug === slug) {
              // Add-another-account flow: scope the NEW connection, found by
              // diffing the roster against the ids known before the redirect.
              clearAddAccountLaunch();
              void getConnectedAccounts().then((accounts) => {
                const entry = accounts.find((a) => a.toolkit.slug.toLowerCase() === slug);
                const fresh = (entry?.connections || []).find((c) => !addMark.knownIds.includes(c.id));
                setScopePickerConnectionId(fresh?.id || null);
                setScopePickerSlug(slug);
              }).catch(() => setScopePickerSlug(slug));
            } else if (scope.grandfathered !== false) {
              // First-time connect with no explicit choice yet. If the user
              // ALREADY chose folders (e.g. in onboarding seconds ago), the
              // scope doc says so — asking again here was the owner-reported
              // "it had me choose the folders again" (2026-08-12).
              setScopePickerSlug(slug);
            }
          })
          .catch(() => {
            // Scope read failed — still offer the sheet with defaults.
            setScopePickerSlug(slug);
          });
      }
    }
    // In-chat "manage access & folders" deep link (2026-08-14 owner repro:
    // the button navigated to bare /integrations, which from the on-page
    // chat overlay just looked like a refresh that did nothing). ?manage=
    // lands directly in the right editor: folder-scope picker for storage
    // apps, the access-tier menu for everything else.
    const manage = params.get('manage');
    if (manage) {
      const slug = manage.toLowerCase();
      window.history.replaceState({}, '', '/integrations');
      if (supportsFolderScope(slug)) {
        getStorageScope(slug)
          .then((scope) => {
            setStorageScopes((prev) => ({ ...prev, [slug]: scope }));
            setScopePickerSlug(slug);
          })
          .catch(() => setScopePickerSlug(slug));
      } else {
        // The open-sheet effect (below) fetches for ANY slug, connected or
        // not — this deep link used to open a permanently dead sheet.
        setAccessMenuSlug(slug);
      }
    }
    // ?setup=<slug> (2026-08-24): the chat needs-setup card's "finish setup"
    // pill — opens the SAME quick setup form the panel's own connect flow
    // uses, pre-fetching the toolkit's required fields. An app that turns
    // out to need nothing falls through to the access sheet instead of a
    // dead end.
    const setup = params.get('setup');
    if (setup) {
      const slug = setup.toLowerCase();
      window.history.replaceState({}, '', '/integrations');
      void getConnectRequiredFields(slug)
        .then((meta) => {
          if (meta.required.length > 0) {
            setSetupPrompt({
              slug,
              fields: meta.required,
              values: Object.fromEntries(meta.required.map((f) => [f.name, ''])),
              ...(meta.help ? { help: meta.help } : {}),
            });
          } else {
            setAccessMenuSlug(slug);
          }
        })
        .catch(() => setAccessMenuSlug(slug));
    }
  }, [loadData]);

  // One click kills the whole app (2026-08-12 incident): the toolkit-level
  // delete removes EVERY connection for the slug — duplicate records
  // included — instead of one record per click.
  const handleDisconnect = async (toolkitSlug: string) => {
    const slug = toolkitSlug.toLowerCase();
    try {
      setDisconnectingSlug(slug);
      setError(null);
      await disconnectToolkit(slug);
      // Optimistically flip this app's connected state right away, then
      // refetch to reconcile — the X reads "gone" on the click, not after
      // the round trip.
      setTwoSets((prev) => ({
        ...prev,
        organization: (prev.organization || []).filter(
          (r) => r.toolkit.slug.toLowerCase() !== slug
        ),
      }));
      // Drop the slug's cached scope/tier so a later reconnect refetches.
      setStorageScopes((prev) => {
        const { [slug]: _dropped, ...rest } = prev;
        return rest;
      });
      scopeFetchesInFlight.current.delete(slug);
      setAccessDetails((prev) => {
        const { [slug]: _dropped, ...rest } = prev;
        return rest;
      });
      accessFetchesInFlight.current.delete(slug);
      setAccessMenuSlug((prev) => (prev === slug ? null : prev));
      invalidateConnectedAppsCache();
      await loadData();
      onConnectionChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect');
    } finally {
      setDisconnectingSlug(null);
    }
  };

  // Personal ✕ (two-set model): remove the user's OWN connections only —
  // per-id deletes over the tile's personal roster. NEVER disconnectToolkit
  // here: the toolkit-level delete also kills org connections. Ownership is
  // server-enforced, so no canManage gate.
  const handleDisconnectPersonal = async (toolkitSlug: string) => {
    const slug = toolkitSlug.toLowerCase();
    const tile = personalCollapsed.find((a) => a.toolkit.slug.toLowerCase() === slug);
    const ids = (tile?.connections || []).map((c) => c.id);
    try {
      setDisconnectingSlug(slug);
      setError(null);
      for (const id of ids) {
        await disconnectAccount(id);
      }
      // Optimistic flip (same pattern as the org disconnect), then refetch.
      setTwoSets((prev) => ({
        ...prev,
        personal: prev.personal.filter((r) => r.toolkit.slug.toLowerCase() !== slug),
      }));
      invalidateConnectedAppsCache();
      await loadData();
      onConnectionChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect');
    } finally {
      setDisconnectingSlug(null);
    }
  };

  // Per-connection removal (ManageAppModal roster rows, 2026-08-18): ONE
  // account of a multi-account app. Personal rows are the user's own
  // (ownership server-enforced); org rows are manage-gated in the UI.
  const handleDisconnectConnection = async (toolkitSlug: string, connectionId: string) => {
    const slug = toolkitSlug.toLowerCase();
    try {
      setDisconnectingSlug(slug);
      setError(null);
      await disconnectAccount(connectionId);
      invalidateConnectedAppsCache();
      await loadData();
      onConnectionChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to remove the account');
    } finally {
      setDisconnectingSlug(null);
    }
  };

  // Per-app management popup (2026-08-18): roster, add-account, access,
  // folders, share, disconnect — one place.
  const [manageSlug, setManageSlug] = useState<string | null>(null);

  // W-R3 (owner ruling 2026-08-25): in the PERSONAL workspace the share
  // control is a multiple-choice of the user's other organizations; in an
  // ORG workspace it stays the single share-with-this-organization toggle.
  // null = org context (or unresolved) — existing behavior.
  const [orgShareTargets, setOrgShareTargets] = useState<Array<{ accountId: string; label: string }> | null>(null);
  useEffect(() => {
    let alive = true;
    const uid = user?.id ?? null;
    if (!uid) return undefined;
    void import('../workspaceOptionsCache').then(({ fetchWorkspaceOptionsCached }) =>
      fetchWorkspaceOptionsCached(uid),
    ).then((options) => {
      if (!alive) return;
      const active = options.find((o) => o.isActive);
      if (!active || active.accountId !== uid) return; // org context — single toggle
      const targets = options
        .filter((o) => o.accountId !== uid)
        .map((o) => ({ accountId: o.accountId, label: o.displayName || 'workspace' }));
      if (targets.length > 0) setOrgShareTargets(targets);
    }).catch(() => { /* picker degrades to the single toggle */ });
    return () => { alive = false; };
  }, [user?.id]);

  // Share/unshare a personal tile with the organization (integrations.share).
  // Per-id over the tile's roster; optimistic chip flip + refetch; a 403
  // reverts and names the permission inline on the tile.
  const [sharingSlug, setSharingSlug] = useState<string | null>(null);
  const [shareError, setShareError] = useState<{ slug: string; message: string } | null>(null);

  const handleShare = async (toolkitSlug: string, share: boolean, targetAccountId?: string) => {
    const slug = toolkitSlug.toLowerCase();
    const tile = personalCollapsed.find((a) => a.toolkit.slug.toLowerCase() === slug);
    const ids = (tile?.connections || []).map((c) => c.id);
    if (ids.length === 0) return;
    setSharingSlug(slug);
    setShareError(null);
    setTwoSets((prev) => ({
      ...prev,
      personal: prev.personal.map((r) =>
        ids.includes(r.id) ? { ...r, sharedWithOrg: share } : r
      ),
    }));
    try {
      // W-R3: an explicit target shares to that workspace; the ambient
      // quick-toggle in the PERSONAL workspace fans out to every linked org
      // (the manage popup offers per-org control); in an org workspace the
      // ambient form keeps sharing to the current workspace.
      const targets = targetAccountId
        ? [targetAccountId]
        : orgShareTargets
          ? orgShareTargets.map((t) => t.accountId)
          : [undefined];
      for (const id of ids) {
        for (const target of targets) {
          await shareConnection(id, share, target);
        }
      }
      await loadData();
    } catch (err: any) {
      // Revert the optimistic flip; name the permission on the RBAC denial.
      setTwoSets((prev) => ({
        ...prev,
        personal: prev.personal.map((r) =>
          ids.includes(r.id) ? { ...r, sharedWithOrg: !share } : r
        ),
      }));
      const msg = String(err?.message || '');
      setShareError({
        slug,
        message: /permission/i.test(msg)
          ? 'sharing requires the integrations.share permission'
          : msg || 'failed to update sharing',
      });
    } finally {
      setSharingSlug(null);
    }
  };

  // Setup-fields prompt (2026-08-14, WhatsApp WABA incident): some apps need
  // extra values before OAuth (WABA ID, subdomain, store name, …). The
  // toolkit's own catalog metadata drives the form — zero per-app code — and
  // connect errors are never shown as raw JSON.
  const [setupPrompt, setSetupPrompt] = useState<SetupPromptState | null>(null);

  // Popup connect (2026-08-18 UX overhaul): this page no longer full-page
  // redirects for OAuth — the SAME popup ritual as chat and onboarding, via
  // the shared engine. The user never leaves the page; the popup auto-closes
  // on completion; the status line below covers reopen/cancel/timeout.
  const popupFlow = useConnectPopup();

  // Post-connect moment ("done → wow"): ONE compact card — defaults as
  // one-tap chips + a suggested first action. Never a blocking question.
  const [doneCard, setDoneCard] = useState<{
    slug: string;
    displayName: string;
    isStorage: boolean;
    addedAccount?: boolean;
    /** Additive storage connect: the NEW connection the folders chip targets. */
    connectionId?: string | null;
    /** Browser-signin (custom) connection — the same wow moment (2026-08-19). */
    customConnection?: boolean;
    /** Email journey (2026-08-20): the post-connect probe's verdict — the
     *  card upgrades from "connected" to confirmed / mismatch / no-mailbox. */
    verification?: ConnectionVerification | null;
    /** The inbox the user said they were connecting (mismatch comparison). */
    intendedAddress?: string | null;
  } | null>(null);

  // Email journey: capture the intended inbox BEFORE the OAuth window.
  const [emailIntentPrompt, setEmailIntentPrompt] = useState<{
    slug: string;
    addAccount?: { knownIds: string[] };
  } | null>(null);

  // Remembered for the timeout line's "try again".
  const lastConnectRef = useRef<{
    slug: string;
    initFields?: Record<string, string>;
    addAccount?: { knownIds: string[] };
    intent?: { intendedAddress?: string };
  } | null>(null);

  // Admin handoff kit on the pending-admin tile: which tile's note is open,
  // and whether the clipboard copy landed (the textarea stays either way so
  // manual copy always works).
  const [adminNoteSlug, setAdminNoteSlug] = useState<string | null>(null);
  const [adminNoteCopied, setAdminNoteCopied] = useState(false);

  const adminNoteFor = (pending: { toolkitSlug: string; intendedAddress: string | null }): string | null => {
    const provider = providerForEmailToolkitSlug(pending.toolkitSlug);
    if (!provider) return null;
    const ownMicrosoftUrl = buildMicrosoftAdminConsentUrlFromClientId(
      twoSets.oauthApps?.microsoftClientId ?? null,
    );
    return buildAdminHandoffNote({
      provider,
      appDisplayName: 'Prest0n',
      adminConsentUrl: provider === 'microsoft' ? ownMicrosoftUrl : null,
      googleClientId: provider === 'google' ? (twoSets.oauthApps?.googleClientId ?? null) : null,
      // Until prest0n's own apps exist, the approval screen names the
      // connection partner — the note must match what the admin will see.
      partnerAppName: (provider === 'microsoft' ? ownMicrosoftUrl : twoSets.oauthApps?.googleClientId) ? null : 'Composio',
      intendedAddress: pending.intendedAddress,
    });
  };

  const runPopupConnect = async (
    toolkitSlug: string,
    initFields?: Record<string, string>,
    addAccount?: { knownIds: string[] },
    intent?: { intendedAddress?: string },
  ) => {
    const slug = toolkitSlug.toLowerCase();
    lastConnectRef.current = { slug: toolkitSlug, initFields, addAccount, intent };
    setError(null);
    setDoneCard(null);
    await popupFlow.start({
      toolkitSlug,
      displayName: displayNameForSlug(toolkitSlug),
      // Two-set model: the active tab decides the identity the OAuth connect
      // lands on — 'personal' (any member) vs 'organization' (manage-gated).
      target: activeView,
      initFields,
      addAccount,
      intent,
      onActive: (info) => {
        void loadData();
        onConnectionChange?.();
        setDoneCard({
          slug,
          displayName: displayNameForSlug(toolkitSlug),
          isStorage: info.isStorage,
          addedAccount: !!addAccount,
          connectionId: info.newConnectionId,
          intendedAddress: intent?.intendedAddress || null,
        });
      },
      onVerified: (verification) => {
        // Upgrade the done card in place; refresh rows so identities and
        // mailbox health paint (the probe persisted them server-side).
        setDoneCard((prev) => (prev && prev.slug === slug ? { ...prev, verification } : prev));
        void loadData();
      },
      onNeedsSetup: (fields, help) => {
        setSetupPrompt({
          slug: toolkitSlug,
          fields,
          values: Object.fromEntries(fields.map((f) => [f.name, ''])),
          ...(help ? { help } : {}),
        });
      },
      onDenied: (serverMessage) => setError(serverMessage),
      // No one-click sign-in for this app (2026-09-02). Not an error — hand
      // it to the panel's own browser lane, which warms the workstation,
      // registers the connection, and opens the in-page sign-in surface (or
      // the chat concierge). The tile's CTA already says this is what the
      // click does; this is the belt for a cold catalog that could not.
      onUnsupported: ({ displayName }) => {
        void handleConnectCustom(displayName);
      },
      onError: (message) => setError(message || 'Failed to initiate connection'),
      onTimeout: () => {
        // C4a: an email-journey timeout WITH a stated inbox is very often
        // the admin-approval wait (days, not minutes) — record it durably so
        // the amber waiting tile survives this tab; the server self-heals it
        // once the connection reports ACTIVE. Fire-and-forget.
        if (isEmailJourneySlug(slug) && intent?.intendedAddress) {
          void recordPendingConnection(slug, displayNameForSlug(toolkitSlug), intent.intendedAddress)
            .then(() => loadData())
            .catch(() => { /* the tile is an enhancement, never the flow */ });
        }
      },
    });
  };

  const handleConnect = async (toolkitSlug: string) => {
    try {
      setConnecting(toolkitSlug);
      setError(null);
      // Lane guard, BEFORE the connect-fields pre-check (2026-09-02). The
      // tile's own CTA already routes browser-lane apps, but six other
      // affordances reach this function — "+ add account" on both rows, the
      // manage modal, the omnisearch row, the status line's try-again, the
      // mismatch remedy — and every one of them used to run the un-mintable
      // path. Worse for API-key-shaped apps: getConnectRequiredFields falls
      // back to the first auth_config_details entry when nothing is managed,
      // so the panel collected an API key in a setup dialog and THEN failed.
      const lane = peekConnectLane(toolkitSlug);
      if (lane !== 'unknown' && !laneIsDirect(lane)) {
        if (lane === 'browser') {
          await handleConnectCustom(displayNameForSlug(toolkitSlug));
        } else {
          // 'none' — a no_auth app. The tile renders "ready to use" instead
          // of a button, but six other affordances reach this function, and
          // a silent return there is a dead click. Say what is true.
          setError(laneExplainer(lane, displayNameForSlug(toolkitSlug)));
        }
        return;
      }
      // Ask the catalog FIRST whether this app needs setup values — prompting
      // up front beats a failed round-trip. Metadata fetch is best-effort:
      // if it fails, the connect attempt's own missing_fields fallback (the
      // engine's needs-setup path) still catches the case.
      const meta = await getConnectRequiredFields(toolkitSlug).catch(() => ({ required: [] as ConnectInitField[], help: undefined }));
      if (meta.required.length > 0) {
        setSetupPrompt({
          slug: toolkitSlug,
          fields: meta.required,
          values: Object.fromEntries(meta.required.map((f) => [f.name, ''])),
          ...(meta.help ? { help: meta.help } : {}),
        });
        return;
      }
      // Email journey (2026-08-20): capture the intended inbox BEFORE the
      // OAuth window — the prompt's continue/skip re-enters the popup flow.
      if (isEmailJourneySlug(toolkitSlug)) {
        setEmailIntentPrompt({ slug: toolkitSlug });
        return;
      }
      await runPopupConnect(toolkitSlug);
    } catch (err: any) {
      setError(err.message || 'Failed to initiate connection');
    } finally {
      setConnecting(null);
    }
  };

  const submitSetupPrompt = async () => {
    if (!setupPrompt) return;
    const missing = setupPrompt.fields.filter((f) => !(setupPrompt.values[f.name] || '').trim());
    if (missing.length > 0) {
      setSetupPrompt({ ...setupPrompt, note: `Please fill in: ${missing.map((f) => f.displayName).join(', ')}` });
      return;
    }
    const { slug, values } = setupPrompt;
    // The submit click is a fresh user gesture — the popup opens inside it.
    setSetupPrompt(null);
    await runPopupConnect(slug, values);
  };

  // Add another account — EVERY connected app, BOTH views (2026-08-18: the
  // personal view finally has the affordance too; ownership of your own
  // accounts is server-enforced). The popup path diffs the roster itself;
  // the localStorage mark stays for the manual-link fallback's return.
  const handleAddAccount = (slug: string, knownIds: string[]) => {
    markAddAccountLaunch(slug, knownIds);
    if (isEmailJourneySlug(slug)) {
      setEmailIntentPrompt({ slug, addAccount: { knownIds } });
      return;
    }
    void runPopupConnect(slug, undefined, { knownIds });
  };

  // Timeout line's "try again" — repeat the exact same launch, intent included.
  const retryLastConnect = () => {
    const last = lastConnectRef.current;
    if (!last) return;
    void runPopupConnect(last.slug, last.initFields, last.addAccount, last.intent);
  };

  // Mismatch card's "retry as the right inbox": remove the wrong-identity
  // connection, then relaunch with the SAME intent so the coach re-pins it.
  const retryConnectAs = async (slug: string, connectionId: string | null, intendedAddress: string) => {
    setDoneCard(null);
    if (connectionId) {
      try { await disconnectAccount(connectionId); } catch { /* best-effort — relaunch regardless */ }
      void loadData();
    }
    await runPopupConnect(slug, undefined, undefined, { intendedAddress });
  };

  const isConnected = (slug: string) =>
    connectedAccounts.some((a) =>
      a.toolkit.slug.toLowerCase() === slug.toLowerCase() && a.status?.toUpperCase() === 'ACTIVE'
    );

  /**
   * Does this catalog toolkit already have a Prest0n-browser sign-in?
   * (2026-09-02.) Custom connections are keyed by the app NAME the user
   * typed, never by a Composio slug, so `isConnected` above cannot see them.
   * Without this join, every browser-lane tile kept offering a sign-in the
   * user had just completed — and after routing ~1,326 catalog apps into
   * that lane, that would have been most of the page. Matched on the same
   * normalization the registration uses, over both the toolkit's display
   * name and its slug, so "Google Chat" finds `custom-google_chat`.
   */
  const browserConnectionFor = (tk: ComposioToolkit): CustomConnection | null => {
    const candidates = new Set(
      [customSlugForName(tk.name || ''), customSlugForName(tk.slug || '')].filter(Boolean),
    );
    if (candidates.size === 0) return null;
    return customConnections.find((c) => candidates.has(String(c.slug || ''))) || null;
  };

  const canManage = hasPermission('integrations.manage');
  const canShare = hasPermission('integrations.share');

  // The custom lane (omnisearch bottom row, 2026-08-18 — replaces the old
  // exact-match hero): register the typed system and open the LARGE in-page
  // sign-in surface right here — the user never leaves the Integrations
  // page. Credentials are typed into the real provider page inside prest0n's
  // own browser — never stored by this panel. Registration is explicit —
  // only this row's click, never a search side effect.

  const handleConnectCustom = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || customBusy) return;
    // Connect-intent warmup (2026-08-31 instant-connect): the custom row's
    // click is explicit intent for THIS app — start the workstation boot
    // kick, transport warm, destination research, and desktop pre-create
    // now, so the concierge turn (or the direct modal below) opens onto a
    // session that already exists.
    void import('./connectIntentWarmup')
      .then(({ warmUpConnectIntent }) =>
        warmUpConnectIntent({ appName: trimmed, preCreateSession: true })
      )
      .catch(() => undefined);
    // ALL non-direct connections go through the CHAT concierge (2026-08-18
    // owner directive): a fresh thread with the ask already SENT, docked
    // LEFT so this page stays visible beside it — the standard orchestrator
    // brain researches the best connection method and comes back leading
    // with one recommendation; the sign-in surfaces open from there.
    // The shared handshake (connectCustomFlow.requestConnectConcierge) is
    // the same one onboarding and the assisted catalog tiles use; without a
    // mounted chat (bare embeds, tests) the direct register + sign-in modal
    // path below keeps the lane from dead-ending.
    const { requestConnectConcierge, registerCustomBrowserConnection } = await import('./connectCustomFlow');
    if (requestConnectConcierge(trimmed)) {
      setSearchQuery('');
      return;
    }
    // Direct fallback: the surface opens on the CLICK, registration resolves
    // in parallel (2026-08-31 — the same teardown beginCustomConnectFlow got
    // on 2026-08-26; this path still held the modal behind a full register +
    // list round trip, 0.3-4s of a button that looked ignored). The slug is
    // merged into the open modal when it lands — the modal's access chips
    // render from that prop without a remount.
    setError(null);
    setSearchQuery('');
    setCustomBusy(true);
    try {
      // One registration path + one scope policy (2026-08-19): the browser
      // lane is strictly PERSONAL in V1 — the old scope=activeView write
      // silently created org-scoped browser connections from the org tab.
      // The surface opens on the click and takes the slug when it lands —
      // beginCustomConnectFlow owns both halves (2026-09-03), so this page no
      // longer carries its own copy of that sequence.
      const { beginCustomConnectFlow } = await import('./connectCustomFlow');
      await beginCustomConnectFlow({ appName: trimmed });
      const fresh = await listCustomConnections().catch(() => null);
      if (fresh) setCustomConnections(fresh);
    } catch (err: any) {
      // The surface is already open and usable — registration failure only
      // means the connected-tile promotion has no save target until a
      // manager registers it (same posture as the chat lane).
      setError(err?.message || `Could not register ${trimmed}`);
    } finally {
      setCustomBusy(false);
    }
  };

  const handleRemoveCustomConnection = async (slug: string) => {
    try {
      await removeCustomConnection(slug);
      setCustomConnections((prev) => prev.filter((c) => c.slug !== slug));
    } catch (err: any) {
      setError(err?.message || 'Could not remove the connection');
    }
  };

  // Display name for a slug (2026-08-11, used by the scope sheet): catalog name
  // → connected-account name → prettified slug. Same cleanup as the summary bar.
  const displayNameForSlug = (slug: string): string => {
    const lower = slug.toLowerCase();
    const tk = toolkits.find((t) => t.slug.toLowerCase() === lower);
    if (tk?.name) return tk.name;
    const acct = connectedAccounts.find((a) => a.toolkit.slug.toLowerCase() === lower);
    if (acct && acct.toolkit.name && acct.toolkit.name !== 'Unknown') return acct.toolkit.name;
    return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Full-catalog directory (2026-08-16, governance plan §8 Phase 3): the
  // backend paginates the whole Composio catalog (~1,209 toolkits); the grid
  // is driven by the omnisearch + family chips. 'featured' with no search
  // keeps the curated default; a search ranks the FULL catalog through the
  // shared scored matcher (appSearch.ts), capped at 60 rendered results.
  const RESULT_CAP = 60;

  // Filter connected accounts to only active ones (show all connected, even non-curated)
  const visibleConnectedAccounts = connectedAccounts.filter(
    (a) => a.status?.toUpperCase() === 'ACTIVE'
  );

  const searchTerm = searchQuery.trim().toLowerCase();
  // 'all apps' — the explicit browse-everything chip, so the full catalog is
  // reachable without typing a search (2026-08-16 live-test).
  const ALL_CATALOG_FAMILY = 'all apps';
  // Category membership spans the FULL catalog (2026-08-16 live-test #2).
  const matchesFamily = (tk: ComposioToolkit): boolean => {
    if (!selectedCategory) return true; // 'featured' default
    if (selectedCategory === ALL_CATALOG_FAMILY) return true;
    return familyForToolkit(tk) === selectedCategory;
  };

  // Ranked search through the shared matcher: exact → synonym → prefix →
  // substring → typo rescue. Search results keep the MATCHER's order (best
  // first); browse views keep the curated most-likely-first sort.
  const searchRanked = searchTerm ? searchToolkits(searchQuery, toolkits) : null;
  const matchedToolkits = searchRanked
    ? searchRanked.map((r) => r.tk).filter(matchesFamily)
    : (!selectedCategory
        ? toolkits.filter((tk) => FEATURED_TOOLKITS.includes(tk.slug))
        : toolkits.filter(matchesFamily)
      ).sort(sortByLikelihood);
  // Owner feedback 2026-08-18: a search with only weak (substring/typo) hits
  // must put the CUSTOM LANE first — the grid below is "maybe related", not
  // the answer. Strong = exact/synonym/prefix.
  const bestSearchScore = searchRanked && searchRanked.length > 0 ? searchRanked[0].score : null;
  const searchHasStrongMatch = bestSearchScore !== null && bestSearchScore <= 2;
  const totalMatches = matchedToolkits.length;
  const resultsTruncated = totalMatches > RESULT_CAP;
  // Search results show ONLY what matched (owner feedback 2026-08-18: the
  // old always-render-connected union filled zero-match searches with
  // unrelated green tiles). Connected apps have their own section on top,
  // so nothing is ever lost from view.
  const filteredToolkits = resultsTruncated ? matchedToolkits.slice(0, RESULT_CAP) : matchedToolkits;

  // The unclassified tail: toolkits no curated placement AND no catalog
  // category keyword claims (familyForToolkit's fallback family).
  const uncategorizedCount = toolkits.filter(
    (tk) => familyForToolkit(tk) === MORE_APPS_FAMILY
  ).length;

  if (toolkitsLoading) {
    return (
      <div className={`integrations-panel ${className}`} style={{ padding: '1.5rem' }}>
        {/* Skeleton loading grid */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', height: '38px', borderRadius: '8px', background: panelTheme.grayBg }} />
          {[1, 2, 3].map(i => (
            <div key={i} style={{ width: '80px', height: '32px', borderRadius: '16px', background: panelTheme.grayBg }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{
              border: `1px solid ${panelTheme.grayBorder}`, borderRadius: panelTheme.radiusCard, padding: '1rem',
              background: '#fff',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: panelTheme.grayBg }} />
                <div style={{ width: '80px', height: '14px', borderRadius: '4px', background: panelTheme.grayBg }} />
              </div>
              <div style={{ width: '100%', height: '10px', borderRadius: '4px', background: panelTheme.grayBg, marginBottom: '0.5rem' }} />
              <div style={{ width: '70%', height: '10px', borderRadius: '4px', background: panelTheme.grayBg, marginBottom: '0.75rem' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ width: '40px', height: '10px', borderRadius: '4px', background: panelTheme.grayBg }} />
                <div style={{ width: '65px', height: '28px', borderRadius: '6px', background: panelTheme.grayBg }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // The custom row renders whenever the user typed something — the lane for
  // anything not listed. It sits UNDER the ranked results: catalog hits are
  // always visible above it, so a typo'd catalog app can never silently go
  // down the custom lane (the old hero's exact-match bug).
  const customRow = searchTerm ? (
    <div
      style={{
        marginTop: '0.75rem', padding: '0.8rem 1rem',
        background: 'linear-gradient(135deg, #1C1917 0%, #2b2521 100%)',
        border: `1px solid ${panelTheme.gold}`, borderRadius: panelTheme.radiusCard,
        color: '#F5F0E6', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: '0.84rem', color: '#D8CFBE', flex: 1, minWidth: '220px', lineHeight: 1.5 }}>
        {searchHasStrongMatch ? 'something else? ' : 'not in the catalog — '}
        Prest0n&rsquo;s own browser can sign in to any system with a web login.
      </span>
      <button
        onClick={() => void handleConnectCustom(searchQuery.trim())}
        disabled={customBusy}
        data-testid="connect-custom-row"
        style={{
          padding: '0.45rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
          background: panelTheme.gold, color: panelTheme.ink, fontWeight: 700,
          fontSize: '0.84rem', cursor: customBusy ? 'default' : 'pointer',
          opacity: customBusy ? 0.6 : 1,
        }}
      >
        {customBusy ? 'connecting…' : `connect "${searchQuery.trim()}" with Prest0n's browser →`}
      </button>
      {/* Members register personal browser connections themselves now
          (2026-08-19 server change) — the old manage-permission warning
          described a dead end that no longer exists. */}
    </div>
  ) : null;

  return (
    <div className={`integrations-panel ${className}`} style={{ padding: '1.5rem' }}>
      {/* Scoped polish rules (2026-08-18): inline styles can't express
          :hover / :focus-visible — the page's interactive affordances live
          here, warm-gold and motion-respectful, nothing else global. */}
      <style>{`
        .integrations-panel button:focus-visible,
        .integrations-panel input:focus-visible,
        .integrations-panel a:focus-visible {
          outline: 2px solid ${panelTheme.gold};
          outline-offset: 2px;
        }
        .integrations-panel [data-testid^="app-tile-"] {
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
        }
        .integrations-panel [data-testid^="app-tile-"]:hover {
          border-color: ${panelTheme.gold};
          box-shadow: 0 4px 14px rgba(30, 25, 10, 0.08);
        }
        .integrations-panel [data-testid^="connected-tile-"] {
          transition: box-shadow 0.15s ease;
        }
        .integrations-panel [data-testid^="connected-tile-"]:hover {
          box-shadow: 0 4px 14px rgba(30, 25, 10, 0.08);
        }
        @media (prefers-reduced-motion: reduce) {
          .integrations-panel [data-testid^="app-tile-"],
          .integrations-panel [data-testid^="connected-tile-"] {
            transition: none;
          }
        }
      `}</style>

      {/* Two-set toggle (2026-08-13 owner directive): "my connections" = the
          member's own accounts; "organization" = workspace-owned + member-shared
          connections. The org tab exists only when the server says the caller
          may see that set (orgVisible). */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {(['personal', 'organization'] as IntegrationsView[])
            .filter((mode) => mode === 'personal' || orgVisible)
            .map((mode) => {
              const active = activeView === mode;
              return (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  style={{
                    // Warm palette (2026-08-16 owner directive: "blue is not
                    // it") — the tabs match the gold chip language everywhere
                    // else on this page.
                    padding: '0.35rem 0.7rem', borderRadius: '16px', fontSize: '0.82rem',
                    border: active ? `1px solid ${panelTheme.gold}` : `1px solid ${panelTheme.grayBorder}`,
                    background: active ? panelTheme.goldTint : '#fff',
                    color: active ? panelTheme.goldInk : panelTheme.graySlate,
                    cursor: 'pointer', fontWeight: active ? 600 : 400,
                  }}
                >
                  {mode === 'personal' ? 'my connections' : 'organization'}
                </button>
              );
            })}
        </div>
        {!orgVisible && (
          <div style={{ fontSize: '0.75rem', color: panelTheme.grayFaint, marginTop: '0.4rem' }}>
            organization integrations are managed by workspace admins
          </div>
        )}
      </div>

      {successMessage && (
        <div style={{
          background: panelTheme.successBg, border: '1px solid #BBF7D0', borderRadius: '8px',
          padding: '0.75rem 1rem', marginBottom: '1rem', color: panelTheme.success, fontSize: '0.9rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>✅ {successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: panelTheme.success, fontSize: '1.1rem' }}>×</button>
        </div>
      )}

      {error && (
        <div style={{
          background: panelTheme.dangerBg, border: '1px solid #FECACA', borderRadius: '8px',
          padding: '0.75rem 1rem', marginBottom: '1rem', color: panelTheme.danger, fontSize: '0.9rem',
        }}>
          {error}
        </div>
      )}

      {/* Subtle refetch hint (2026-08-12): refetches keep the grid rendered —
          this line is the only visible signal, so the X stays mounted and a
          focus-triggered refresh can never eat a click. */}
      {refreshing && (
        <div style={{ fontSize: '0.75rem', color: panelTheme.grayFaint, marginBottom: '0.5rem' }}>
          Refreshing…
        </div>
      )}

      {/* In-flight connect status (2026-08-18 reliability doctrine): always
          visible where the user is; accidental popup close → [reopen] (same
          window name — never blocked); timeout resolves to a clear retry. */}
      <ConnectStatusLine
        connectingApp={popupFlow.connectingSlug ? displayNameForSlug(popupFlow.connectingSlug) : null}
        canReopen={popupFlow.canReopen}
        timedOutApp={popupFlow.timedOutSlug ? displayNameForSlug(popupFlow.timedOutSlug) : null}
        onReopen={popupFlow.reopen}
        onCancel={popupFlow.cancel}
        onRetry={retryLastConnect}
        onDismissTimeout={popupFlow.dismissTimeout}
      />

      {/* Popup-blocked fallback: a direct link click is never blocked, and
          the same window name keeps the poll's completion path intact. */}
      {popupFlow.manualConnect && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
          padding: '0.6rem 1rem', marginBottom: '1rem',
          background: panelTheme.surfaceGoldTint, border: `1px solid ${panelTheme.gold}`,
          borderRadius: panelTheme.radiusInput, fontSize: '0.84rem', color: panelTheme.goldInk,
        }}>
          <span>your browser blocked the sign-in window —</span>
          <a
            href={popupFlow.manualConnect.url}
            target="prest0n-connect"
            data-testid="manual-connect-link"
            style={{ fontWeight: 700, color: panelTheme.ink, textDecoration: 'underline', textTransform: 'lowercase' }}
          >
            open the {displayNameForSlug(popupFlow.manualConnect.slug)} sign-in
          </a>
        </div>
      )}

      {/* Post-connect moment: defaults as one-tap chips + the suggested
          first action — connect → try → result. Storage apps get a
          non-blocking folders chip instead of the old auto-opened picker. */}
      {doneCard && (
        <ConnectionDoneCard
          slug={doneCard.slug}
          displayName={doneCard.displayName}
          isStorage={doneCard.isStorage}
          addedAccount={doneCard.addedAccount}
          customConnection={doneCard.customConnection}
          verification={doneCard.verification}
          intendedAddress={doneCard.intendedAddress}
          onRetryAs={(intendedAddress) => {
            void retryConnectAs(doneCard.slug, doneCard.connectionId || null, intendedAddress);
          }}
          onOpenAccess={() => {
            setAccessMenuSlug(doneCard.slug);
            setDoneCard(null);
          }}
          onOpenFolders={() => {
            setScopePickerConnectionId(doneCard.addedAccount ? doneCard.connectionId || null : null);
            setScopePickerSlug(doneCard.slug);
            setDoneCard(null);
          }}
          onDismiss={() => setDoneCard(null)}
        />
      )}

      {emailIntentPrompt && (
        <EmailIntentPrompt
          slug={emailIntentPrompt.slug}
          displayName={displayNameForSlug(emailIntentPrompt.slug)}
          onContinue={(intendedAddress) => {
            const { slug, addAccount } = emailIntentPrompt;
            setEmailIntentPrompt(null);
            void runPopupConnect(
              slug,
              undefined,
              addAccount,
              intendedAddress ? { intendedAddress } : undefined,
            );
          }}
          onCancel={() => setEmailIntentPrompt(null)}
        />
      )}

      {/* What-to-expect note (task #43, 2026-08-10): every connection runs
          through Composio, and the provider's consent screen names it
          ("continue to Composio") — users who've never heard the name read
          that as a red flag. Set the expectation up front, once, for every
          integration on this page. */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '1.25rem',
        padding: '0.7rem 1rem', background: panelTheme.surfaceGoldTint,
        border: `1px solid ${panelTheme.border}`, borderRadius: '8px',
        fontSize: '0.84rem', color: panelTheme.goldInk, lineHeight: 1.55,
      }}>
        <span aria-hidden="true">🔐</span>
        <span>
          The sign-in screen will say <strong>&ldquo;continue to Composio&rdquo;</strong> —
          that&rsquo;s expected. Composio is Prest0n&rsquo;s secure connection partner:
          you sign in with the provider directly, your password never touches
          Prest0n, and you can disconnect any app here.
        </span>
      </div>

      {/* ── C4a: pending-admin waits — a connect that timed out with a stated
          inbox (usually Microsoft admin approval, which can take days). The
          server self-heals these once the connection reports ACTIVE; retry
          relaunches with the SAME intended inbox. */}
      {Object.values(twoSets.pendingConnections || {}).map((pending) => (
        <div
          key={`pending-${pending.toolkitSlug}`}
          data-testid={`pending-approval-${pending.toolkitSlug}`}
          style={{
            display: 'flex', flexDirection: 'column', gap: '0.5rem',
            padding: '0.85rem 1.05rem', marginBottom: '0.9rem',
            background: panelTheme.goldTint,
            border: `1px solid ${panelTheme.goldInk}`,
            borderRadius: panelTheme.radiusCard,
          }}
        >
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: panelTheme.goldInk }}>
            ⏳ {pending.displayName} — waiting on an admin&rsquo;s approval
          </div>
          <div style={{ fontSize: '0.8rem', color: panelTheme.grayText, lineHeight: 1.5 }}>
            the sign-in{pending.intendedAddress ? <> for <strong>{pending.intendedAddress}</strong></> : null} didn&rsquo;t
            finish — usually because an admin has to approve the app first, which can take a while.
            the admin approves it from their admin console — they never sign in with their own
            account. once they&rsquo;ve approved, try again below.
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              data-testid={`pending-approval-retry-${pending.toolkitSlug}`}
              onClick={() => {
                void runPopupConnect(
                  pending.toolkitSlug,
                  undefined,
                  undefined,
                  pending.intendedAddress ? { intendedAddress: pending.intendedAddress } : undefined,
                );
              }}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: panelTheme.radiusPill, border: 'none',
                background: panelTheme.ink, color: '#fff', fontWeight: 700,
                fontSize: '0.78rem', cursor: 'pointer', textTransform: 'lowercase',
              }}
            >
              approved — try again{pending.intendedAddress ? ` as ${pending.intendedAddress}` : ''}
            </button>
            <button
              data-testid={`pending-approval-dismiss-${pending.toolkitSlug}`}
              onClick={() => {
                void clearPendingConnection(pending.toolkitSlug)
                  .then(() => loadData())
                  .catch(() => { /* the next accounts load re-syncs */ });
              }}
              style={{
                background: 'none', border: 'none', color: panelTheme.grayText,
                fontSize: '0.78rem', cursor: 'pointer', textTransform: 'lowercase',
                textDecoration: 'underline',
              }}
            >
              dismiss
            </button>
            <button
              data-testid={`pending-admin-note-toggle-${pending.toolkitSlug}`}
              onClick={() => {
                const note = adminNoteFor(pending);
                if (!note) return;
                if (adminNoteSlug === pending.toolkitSlug) {
                  setAdminNoteSlug(null);
                  return;
                }
                setAdminNoteSlug(pending.toolkitSlug);
                setAdminNoteCopied(false);
                try {
                  void navigator.clipboard?.writeText(note)
                    .then(() => setAdminNoteCopied(true))
                    .catch(() => { /* the textarea below covers manual copy */ });
                } catch { /* no clipboard API — manual copy via the textarea */ }
              }}
              style={{
                background: 'none', border: 'none', color: panelTheme.goldInk,
                fontSize: '0.78rem', cursor: 'pointer', textTransform: 'lowercase',
                textDecoration: 'underline',
              }}
            >
              {adminNoteSlug === pending.toolkitSlug
                ? 'hide the note'
                : adminNoteCopied ? '✓ copied — note for your admin' : 'note for your admin'}
            </button>
          </div>
          {adminNoteSlug === pending.toolkitSlug && adminNoteFor(pending) && (
            <>
              <div style={{ fontSize: '0.75rem', color: panelTheme.grayText }}>
                {adminNoteCopied
                  ? '✓ copied to your clipboard — paste it to your admin (it is also below).'
                  : 'copy this and send it to your admin:'}
              </div>
              <textarea
                readOnly
                data-testid={`pending-admin-note-${pending.toolkitSlug}`}
                value={adminNoteFor(pending) || ''}
                onFocus={(e) => e.currentTarget.select()}
                rows={7}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.7rem',
                  borderRadius: panelTheme.radiusChip, border: `1px solid ${panelTheme.grayBorder}`,
                  fontSize: '0.78rem', color: panelTheme.ink, background: panelTheme.surface,
                  lineHeight: 1.45, resize: 'vertical',
                }}
              />
            </>
          )}
        </div>
      ))}

      {/* ── Connected section (2026-08-18): every connected app in the
          active view — Composio + custom browser-signin systems — as compact
          tiles at the TOP of the page, each one tap from its management
          popup. Replaces the old green name-chip bar and custom-row strip. */}
      <ConnectedSection
        entries={visibleConnectedAccounts}
        customConnections={customConnections}
        toolkits={toolkits}
        canManage={canManage}
        renderAccessChip={renderAccessChip}
        onManage={(slug) => setManageSlug(slug)}
        onFinishCustomSignIn={(c) => {
          // A button that says "finish sign-in" OPENS the sign-in, and starts
          // the thread that picks the journey back up. One owner for both
          // halves and the reasoning behind them: connectCustomFlow
          // .resumeCustomSignIn (Miles, 2026-09-18, the eFax incident).
          void import('./connectCustomFlow').then((m) => m.resumeCustomSignIn(c));
        }}
        onRemoveCustom={(slug) => void handleRemoveCustomConnection(slug)}
        onSetIdentity={async (slug, identity) => {
          // Feature-detected like the access edit beside it: an older server
          // has no setIdentity branch and falls through to the register path,
          // which refuses a body with no name. Only { ok: true } counts.
          try {
            const res = await updateCustomConnectionIdentity(slug, identity);
            if (res?.ok !== true) return false;
          } catch {
            return false;
          }
          const fresh = await listCustomConnections().catch(() => null);
          if (fresh) setCustomConnections(fresh);
          return true;
        }}
      />

      {/* Not-listed hint (2026-08-18 — the old dark hero card, on a diet):
          the omnisearch is the one input for everything, catalog or not. */}
      <div style={{ fontSize: '0.8rem', color: panelTheme.goldInk, marginBottom: '0.4rem' }}>
        <span aria-hidden="true">🌐</span>{' '}
        not listed? Prest0n&rsquo;s own browser can sign in to anything — just search for it.
      </div>

      {/* ONE omnisearch (2026-08-18): ranked catalog results + the custom
          row below them for anything not listed. */}
      <SearchBar
        query={searchQuery}
        onQueryChange={(q) => { setSearchQuery(q); setSelectedCategory(null); }}
        catalogCount={toolkits.length}
        onSubmit={() => {
          // Enter = the custom row, exactly when the custom row LEADS (no
          // strong catalog match — owner ask 2026-08-22). With a strong
          // match the catalog answer leads (2026-08-18 directive) and Enter
          // must not hijack a typo'd catalog app into the custom lane.
          const trimmed = searchQuery.trim();
          if (!trimmed || customBusy || searchHasStrongMatch) return;
          void handleConnectCustom(trimmed);
        }}
      />

      {/* Family filter chips (2026-08-16): 'featured' (curated default) +
          'all apps' (whole catalog) + the families + 'more apps' for the
          unclassified tail. Warm palette on the active chip — never blue. */}
      <CategoryChips
        categories={['featured', ALL_CATALOG_FAMILY, ...Object.keys(TOOLKIT_CATEGORIES), ...(uncategorizedCount > 0 ? [MORE_APPS_FAMILY] : [])]}
        selected={selectedCategory}
        hasSearch={!!searchQuery}
        onSelect={(cat) => {
          setSelectedCategory(cat === 'featured' || selectedCategory === cat ? null : cat);
          setSearchQuery('');
        }}
      />

      {/* ONE state line per view (2026-08-16 review). Warm, lowercase. */}
      {!searchQuery && !selectedCategory && (
        <div style={{ fontSize: '0.8rem', color: panelTheme.goldInk, marginBottom: '0.5rem', fontWeight: 500 }}>
          {toolkits.length > FEATURED_TOOLKITS.length
            ? `showing the ${totalMatches} most-used apps — search or pick a category to explore all ${toolkits.length.toLocaleString()}`
            : `showing ${totalMatches} apps`}
        </div>
      )}
      {selectedCategory && (
        <div style={{ fontSize: '0.8rem', color: panelTheme.goldInk, marginBottom: '0.5rem', fontWeight: 500 }}>
          {selectedCategory} · {totalMatches.toLocaleString()} app{totalMatches !== 1 ? 's' : ''}, most-used first
        </div>
      )}
      {searchQuery && totalMatches > 0 && (
        <div style={{ fontSize: '0.8rem', color: panelTheme.goldInk, marginBottom: '0.5rem', fontWeight: 500 }}>
          {searchHasStrongMatch
            ? `${totalMatches.toLocaleString()} result${totalMatches !== 1 ? 's' : ''} for "${searchQuery}", best match first`
            : `nothing matches "${searchQuery}" directly — maybe related:`}
        </div>
      )}
      {resultsTruncated && (
        <div style={{ fontSize: '0.8rem', color: panelTheme.goldInk, marginBottom: '0.5rem', fontWeight: 500 }}>
          {searchQuery
            ? `showing the best ${RESULT_CAP} of ${totalMatches.toLocaleString()} — refine your search`
            : `showing the ${RESULT_CAP} most-used of ${totalMatches.toLocaleString()} — search to find any specific app`}
        </div>
      )}

      {/* The custom lane leads whenever the catalog has no STRONG answer
          (owner feedback 2026-08-18): the row is the recommendation, the
          weak grid below is only "maybe related". */}
      {searchTerm && !searchHasStrongMatch && customRow}

      {/* Available toolkits grid. Connected apps keep their catalog spot —
          the Connected section above is the at-a-glance surface, so the grid
          no longer force-sorts them first (2026-08-18). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {filteredToolkits.map((tk) => {
          const slug = tk.slug.toLowerCase();
          return (
            <AppTile
              key={tk.slug}
              toolkit={tk}
              connected={isConnected(tk.slug)}
              browserConnection={browserConnectionFor(tk)}
              connecting={connecting === tk.slug || popupFlow.connectingSlug === tk.slug}
              accountsLoading={accountsLoading}
              activeView={activeView}
              canManage={canManage}
              canShare={canShare}
              rbacLoading={rbacLoading}
              disconnecting={disconnectingSlug === slug}
              entry={connectedAccounts.find((a) => a.toolkit.slug.toLowerCase() === slug)}
              storageScope={storageScopes[slug] || null}
              renderAccessChip={renderAccessChip}
              sharePending={sharingSlug === slug}
              shareError={shareError?.slug === slug ? shareError.message : null}
              onOpenScopePicker={(s) => setScopePickerSlug(s)}
              onAddAccount={handleAddAccount}
              onShare={(s, share) => { void handleShare(s, share); }}
              onConnect={(s) => { void handleConnect(s); }}
              onDisconnectOrg={(s) => { void handleDisconnect(s); }}
              onDisconnectPersonal={(s) => { void handleDisconnectPersonal(s); }}
              onBrowserSignIn={(name) => { void handleConnectCustom(name); }}
            />
          );
        })}
      </div>

      {/* With strong matches, the catalog answer leads and the custom lane
          trails — a typo'd catalog app never goes down the custom lane
          unseen. Never a dead "no results" end either way. */}
      {searchTerm && searchHasStrongMatch && customRow}

      {setupPrompt && (
        <SetupFieldsDialog
          prompt={setupPrompt}
          onChange={setSetupPrompt}
          onSubmit={() => { void submitSetupPrompt(); }}
          onCancel={() => setSetupPrompt(null)}
        />
      )}

      {/* The loud "the window will say Composio" explainer now renders via
          the global ComposioConnectCoachHost (App.tsx, 2026-08-20) — the
          engine dispatches it for every surface, chat included, and hides it
          when the sign-in window closes. */}

      {/* Per-app management popup (2026-08-18): roster with per-connection
          removal, add-account, access, folders, share, disconnect-all. */}
      {manageSlug && (() => {
        const entry = connectedAccounts.find(
          (a) => a.toolkit.slug.toLowerCase() === manageSlug,
        );
        if (!entry) return null;
        const storage = supportsFolderScope(manageSlug);
        const scope = storageScopes[manageSlug];
        return (
          <ManageAppModal
            slug={manageSlug}
            displayName={displayNameForSlug(manageSlug)}
            view={activeView}
            connections={entry.connections || []}
            isStorage={storage}
            scopeLabel={(() => {
              // With several accounts the chip reflects (and edits) the
              // PRIMARY connection's folders — each added account keeps its
              // own scope, set right after it connects.
              const multi = (entry.connections?.length || 0) > 1 ? ' (primary account)' : '';
              if (!scope) return `folders${multi}`;
              return scope.mode === 'folders'
                ? `${scope.folders.length} folder${scope.folders.length !== 1 ? 's' : ''} chosen${multi}`
                : `folders: all${multi}`;
            })()}
            shared={entry.sharedWithOrg}
            viaShared={entry.viaShared}
            sharedByName={entry.sharedByName}
            canManage={canManage}
            canShare={canShare && !rbacLoading}
            sharePending={sharingSlug === manageSlug}
            shareError={shareError?.slug === manageSlug ? shareError.message : null}
            disconnecting={disconnectingSlug === manageSlug}
            renderAccessChip={renderAccessChip}
            onAddAccount={() => {
              setManageSlug(null);
              handleAddAccount(manageSlug, (entry.connections || []).map((c) => c.id));
            }}
            onDisconnectConnection={(id) => { void handleDisconnectConnection(manageSlug, id); }}
            onDisconnectAll={() => {
              setManageSlug(null);
              void handleDisconnect(manageSlug);
            }}
            onOpenFolders={() => {
              setManageSlug(null);
              setScopePickerSlug(manageSlug);
            }}
            onShare={(share) => { void handleShare(manageSlug, share); }}
            shareTargets={orgShareTargets?.map((t) => ({
              ...t,
              shared: Boolean((entry as any).sharedTo?.includes(t.accountId)),
            }))}
            onShareTo={(accountId, share) => { void handleShare(manageSlug, share, accountId); }}
            onClose={() => setManageSlug(null)}
          />
        );
      })()}

      {/* The LARGE in-page sign-in surface for non-catalog systems
          (2026-08-18): the user signs into the real provider page inside
          prest0n's own browser without leaving this page; "done — I'm signed
          in" promotes the custom connection and its tile appears in the
          Connected section above. */}
      {/* ONE SURFACE (2026-09-03 owner directive). This page used to mount its
          OWN DesktopSignInModal beside the app-wide DesktopSignInModalHost,
          and the two drifted — the host's surface could take a handed-off
          session, a workspace target and the conversation's language; this
          one could not. The page now opens the same surface every other
          caller opens and hears about completion through the one
          announcement, so a user meets the identical window here, in
          onboarding, and in chat. */}

      {/* Access controls popup (2026-08-18): the shared sheet, enlarged —
          presets + fine-tune with per-class summaries, real example verbs,
          tool counts, and the folder row for storage apps. */}
      {accessMenuSlug && (
        <AccessModal
          slug={accessMenuSlug}
          displayName={displayNameForSlug(accessMenuSlug)}
          detail={accessDetails[accessMenuSlug] ?? null}
          canManage={canManage && !rbacLoading}
          saving={accessSavingSlug === accessMenuSlug}
          folderScopeLabel={isStorageToolkit(accessMenuSlug)
            ? (() => {
                const scope = storageScopes[accessMenuSlug];
                if (!scope) return 'folders';
                return scope.mode === 'folders'
                  ? `${scope.folders.length} folder${scope.folders.length !== 1 ? 's' : ''} chosen`
                  : 'folders: all';
              })()
            : null}
          onOpenFolders={() => {
            const slug = accessMenuSlug;
            setAccessMenuSlug(null);
            setScopePickerSlug(slug);
          }}
          onSelectTier={(tier) => { void handleSetAccessTier(accessMenuSlug, tier); }}
          onSaveProfile={(profile) => { void handleSetAccessProfile(accessMenuSlug, profile); }}
          loadFailed={accessDetailFailedSlug === accessMenuSlug}
          onClose={() => { setAccessMenuSlug(null); setAccessDetailFailedSlug(null); }}
        />
      )}

      {/* Folder scope sheet (2026-08-11): opened after a storage OAuth callback
          and from a card's scope chip. Saving updates the cached scope chip;
          dismissing keeps the current scope (new connections stay grandfathered
          on "Everything" server-side — no dead state). */}
      {scopePickerSlug && (!scopePickerConnectionId || pickerConnectionScope.loaded) && (
        <FolderScopePicker
          appSlug={scopePickerSlug}
          appDisplayName={displayNameForSlug(scopePickerSlug)}
          // A targeted connection (add-another-account flow) shows its OWN
          // stored scope (read back per-connection, 2026-08-20), never the
          // primary's — its choices are its own. A brand-new connection has
          // no stored scope yet and opens on the picker's defaults.
          initialScope={scopePickerConnectionId ? pickerConnectionScope.scope : (storageScopes[scopePickerSlug] || null)}
          connectionId={scopePickerConnectionId || undefined}
          onSaved={(scope) => {
            if (!scopePickerConnectionId) {
              setStorageScopes((prev) => ({ ...prev, [scopePickerSlug]: scope }));
            }
            setScopePickerSlug(null);
            setScopePickerConnectionId(null);
            setSuccessMessage(
              scope.mode === 'folders'
                ? `${displayNameForSlug(scopePickerSlug)}: Prest0n will only use ${scope.folders.length} chosen folder${scope.folders.length !== 1 ? 's' : ''}.`
                : `${displayNameForSlug(scopePickerSlug)}: Prest0n can use everything.`
            );
          }}
          onDismiss={() => { setScopePickerSlug(null); setScopePickerConnectionId(null); }}
        />
      )}
    </div>
  );
};

export default IntegrationsPanel;
